import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckIcon, CrownIcon, ZapIcon, RocketIcon } from 'lucide-react';
import { licensePlans, currentUser, formatCurrency } from '../utils/mockData';
const LicensePlansPage = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const handleSelectPlan = (planId: string) => {
    setSelectedPlan(planId);
  };
  const handlePurchase = () => {
    if (!selectedPlan) return;
    setIsProcessing(true);
    // Simulate payment processing
    setTimeout(() => {
      setIsProcessing(false);
      // In a real app, this would process payment and update the user's license
      alert('License purchased successfully! Your listing limit has been updated.');
      navigate('/dashboard');
    }, 2000);
  };
  const getPlanIcon = (planId: string) => {
    switch (planId) {
      case 'starter':
        return <ZapIcon className="h-8 w-8" data-id="element-834" />;
      case 'professional':
        return <RocketIcon className="h-8 w-8" data-id="element-835" />;
      case 'enterprise':
        return <CrownIcon className="h-8 w-8" data-id="element-836" />;
      default:
        return <ZapIcon className="h-8 w-8" data-id="element-837" />;
    }
  };
  return <div className="bg-gray-50 min-h-screen w-full" data-id="element-838">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-839">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-840">
          <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-841">
            Choose Your License Plan
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-842">
            Select the perfect plan for your real estate business. All plans
            include full access to our platform features.
          </p>
        </div>
        {/* Current License Status */}
        {currentUser.license && <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8 max-w-2xl mx-auto" data-id="element-843">
            <div className="flex items-center justify-between" data-id="element-844">
              <div data-id="element-845">
                <p className="text-sm font-medium text-blue-900" data-id="element-846">
                  Current Plan
                </p>
                <p className="text-lg font-bold text-blue-900 capitalize" data-id="element-847">
                  {licensePlans.find(p => p.id === currentUser.license.licenseId)?.planName}
                </p>
              </div>
              <div className="text-right" data-id="element-848">
                <p className="text-sm text-blue-700" data-id="element-849">
                  {currentUser.license.usedListings} /{' '}
                  {currentUser.license.maxListings} listings used
                </p>
                <p className="text-xs text-blue-600" data-id="element-850">
                  Expires:{' '}
                  {new Date(currentUser.license.expiryDate).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>}
        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-851">
          {licensePlans.map(plan => {
          const isCurrentPlan = currentUser.license?.licenseId === plan.id;
          const isSelected = selectedPlan === plan.id;
          const isProfessional = plan.id === 'professional';
          return <div key={plan.id} className={`relative bg-white rounded-lg shadow-lg overflow-hidden transition-transform duration-300 hover:scale-105 ${isProfessional ? 'border-2 border-blue-600' : 'border border-gray-200'} ${isSelected ? 'ring-2 ring-blue-600' : ''}`} data-id="element-852">
                {isProfessional && <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-853">
                    POPULAR
                  </div>}
                <div className="p-8" data-id="element-854">
                  <div className="flex items-center justify-center mb-4" data-id="element-855">
                    <div className={`p-3 rounded-full ${isProfessional ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}`} data-id="element-856">
                      {getPlanIcon(plan.id)}
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-center text-gray-900 mb-2" data-id="element-857">
                    {plan.planName}
                  </h3>
                  <div className="text-center mb-6" data-id="element-858">
                    <span className="text-4xl font-bold text-gray-900" data-id="element-859">
                      {formatCurrency(plan.price)}
                    </span>
                    <span className="text-gray-600" data-id="element-860">/month</span>
                  </div>
                  <div className="mb-6" data-id="element-861">
                    <p className="text-center text-sm font-medium text-gray-600 mb-4" data-id="element-862">
                      {plan.maxListings} Property Listings
                    </p>
                  </div>
                  <ul className="space-y-3 mb-8" data-id="element-863">
                    {plan.features.map((feature, index) => <li key={index} className="flex items-start" data-id="element-864">
                        <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" data-id="element-865" />
                        <span className="text-sm text-gray-700" data-id="element-866">{feature}</span>
                      </li>)}
                  </ul>
                  {isCurrentPlan ? <button disabled className="w-full bg-gray-300 text-gray-600 font-medium py-3 px-4 rounded-md cursor-not-allowed" data-id="element-867">
                      Current Plan
                    </button> : <button onClick={() => handleSelectPlan(plan.id)} className={`w-full font-medium py-3 px-4 rounded-md transition duration-200 ${isSelected ? 'bg-blue-600 text-white hover:bg-blue-700' : isProfessional ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 text-gray-900 hover:bg-gray-200'}`} data-id="element-868">
                      {isSelected ? 'Selected' : 'Select Plan'}
                    </button>}
                </div>
              </div>;
        })}
        </div>
        {/* Purchase Button */}
        {selectedPlan && <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-md p-6" data-id="element-869">
            <div className="flex items-center justify-between mb-4" data-id="element-870">
              <div data-id="element-871">
                <p className="text-sm text-gray-600" data-id="element-872">Selected Plan</p>
                <p className="text-xl font-bold text-gray-900 capitalize" data-id="element-873">
                  {licensePlans.find(p => p.id === selectedPlan)?.planName}
                </p>
              </div>
              <div className="text-right" data-id="element-874">
                <p className="text-2xl font-bold text-blue-600" data-id="element-875">
                  {formatCurrency(licensePlans.find(p => p.id === selectedPlan)?.price || 0)}
                </p>
                <p className="text-sm text-gray-600" data-id="element-876">per month</p>
              </div>
            </div>
            <button onClick={handlePurchase} disabled={isProcessing} className={`w-full bg-blue-600 text-white font-bold py-3 px-4 rounded-md transition duration-200 ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700'}`} data-id="element-877">
              {isProcessing ? 'Processing...' : 'Purchase License'}
            </button>
            <p className="text-xs text-center text-gray-500 mt-4" data-id="element-878">
              By purchasing, you agree to our terms and conditions. Your license
              will be activated immediately.
            </p>
          </div>}
        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto" data-id="element-879">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8" data-id="element-880">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6" data-id="element-881">
            <div className="bg-white rounded-lg p-6 shadow-sm" data-id="element-882">
              <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-883">
                What happens when I reach my listing limit?
              </h3>
              <p className="text-gray-600" data-id="element-884">
                Once you reach your listing limit, you'll need to upgrade to a
                higher plan or wait until your current listings expire to post
                new ones.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm" data-id="element-885">
              <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-886">
                Can I upgrade or downgrade my plan?
              </h3>
              <p className="text-gray-600" data-id="element-887">
                Yes! You can upgrade your plan at any time. When downgrading,
                the change will take effect at the end of your current billing
                cycle.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm" data-id="element-888">
              <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-889">
                What payment methods do you accept?
              </h3>
              <p className="text-gray-600" data-id="element-890">
                We accept all major credit cards, debit cards, and PayPal. All
                transactions are secure and encrypted.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm" data-id="element-891">
              <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-892">
                Is there a free trial?
              </h3>
              <p className="text-gray-600" data-id="element-893">
                New users get a 7-day free trial of the Starter plan. No credit
                card required to start your trial.
              </p>
            </div>
          </div>
        </div>
        {/* Back to Dashboard */}
        <div className="text-center mt-12" data-id="element-894">
          <Link to="/dashboard" className="text-blue-600 hover:text-blue-800 font-medium" data-id="element-895">
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    </div>;
};
export default LicensePlansPage;